import { api } from "@/lib/api";
import type { ApiResponse } from "@learn-me/types";

export type MaterialCategory =
  | "LECTURA"
  | "PRACTICA"
  | "TRABAJO"
  | "MANUAL"
  | "GUIA"
  | "RECURSO";

export interface DigitalMaterial {
  id: string;
  slug: string;
  title: string;
  description?: string | null;
  coverImageUrl?: string | null;
  fileUrl?: string | null;
  fileName?: string | null;
  fileSizeBytes?: number | null;
  fileType?: string | null;
  category: MaterialCategory;
  isFree: boolean;
  listPrice?: number | string | null;
  salePrice: number | string;
  hasDiscount: boolean;
  currency: string;
  isPublished: boolean;
  position: number;
  courseId?: string | null;
}

export interface MaterialDetail extends DigitalMaterial {
  hasAccess: boolean;
}

export interface AdminMaterial extends DigitalMaterial {
  course?: { id: string; title: string } | null;
  _count?: { purchases: number };
}

export interface PurchasedMaterial {
  purchaseId: string;
  purchasedAt: string;
  material: DigitalMaterial;
}

export interface MaterialInput {
  title: string;
  description?: string | null;
  coverImageUrl?: string;
  fileUrl: string;
  fileName?: string | null;
  fileSizeBytes?: number | null;
  fileType?: string | null;
  category: MaterialCategory;
  isFree: boolean;
  listPrice?: number | null;
  salePrice: number;
  hasDiscount: boolean;
  currency: string;
  isPublished: boolean;
  courseId?: string | null;
}

export const materialsApi = {
  async listPublic(category?: string): Promise<DigitalMaterial[]> {
    const qs = category ? `?category=${encodeURIComponent(category)}` : "";
    const response = await api.get<ApiResponse<DigitalMaterial[]>>(
      `/materials${qs}`,
    );
    return response.data.data!;
  },

  async getBySlug(slug: string): Promise<MaterialDetail> {
    const response = await api.get<ApiResponse<MaterialDetail>>(
      `/materials/${slug}`,
    );
    return response.data.data!;
  },

  // Descarga el archivo vía proxy del API (el archivo se transmite por el
  // servidor; la URL real de Drive nunca llega al navegador).
  async downloadMaterial(materialId: string, filename: string): Promise<void> {
    const response = await api.get(`/materials/${materialId}/download`, {
      responseType: "blob",
    });
    const blobUrl = window.URL.createObjectURL(response.data as Blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename || "material";
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  },

  async listMyMaterials(): Promise<PurchasedMaterial[]> {
    const response = await api.get<ApiResponse<PurchasedMaterial[]>>(
      "/materials/me/purchased",
    );
    return response.data.data!;
  },

  // Admin
  async listAdmin(): Promise<AdminMaterial[]> {
    const response = await api.get<ApiResponse<AdminMaterial[]>>(
      "/materials/admin/all",
    );
    return response.data.data!;
  },

  async getById(id: string): Promise<AdminMaterial> {
    const response = await api.get<ApiResponse<AdminMaterial>>(
      `/materials/admin/${id}`,
    );
    return response.data.data!;
  },

  async create(data: MaterialInput): Promise<DigitalMaterial> {
    const response = await api.post<ApiResponse<DigitalMaterial>>(
      "/materials/admin",
      data,
    );
    return response.data.data!;
  },

  async update(
    id: string,
    data: Partial<MaterialInput>,
  ): Promise<DigitalMaterial> {
    const response = await api.put<ApiResponse<DigitalMaterial>>(
      `/materials/admin/${id}`,
      data,
    );
    return response.data.data!;
  },

  async remove(id: string): Promise<void> {
    await api.delete(`/materials/admin/${id}`);
  },
};

export const MATERIAL_CATEGORY_LABELS: Record<MaterialCategory, string> = {
  LECTURA: "Lectura",
  PRACTICA: "Práctica",
  TRABAJO: "Trabajo",
  MANUAL: "Manual",
  GUIA: "Guía",
  RECURSO: "Recurso descargable",
};
